// Página AdminSuppliers - Gestión de proveedores
// Lista y gestiona todos los proveedores del sistema

import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { getAllSuppliers, deleteSupplier } from '../../services/suppliers.service'
import { useProducts } from '../../hooks/useFirestore'
import Button from '../../components/Button'
import ConfirmDialog from '../../components/ConfirmDialog'
import Toast from '../../components/Toast'
import { formatCurrency } from '../../utils/helpers'
import './AdminSuppliers.css'

/**
 * Página AdminSuppliers
 * Gestión de proveedores para administradores
 */
function AdminSuppliers() {
  const [suppliers, setSuppliers] = useState([])
  const [filteredSuppliers, setFilteredSuppliers] = useState([])
  const [supplierProductCounts, setSupplierProductCounts] = useState({}) // Mapa de email -> cantidad de productos
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [notification, setNotification] = useState(null)

  // Obtener todos los productos para calcular cantidades
  const { products } = useProducts({})

  // Filtros
  const [activeFilter, setActiveFilter] = useState('all') // 'all', 'active', 'inactive'
  const [searchTerm, setSearchTerm] = useState('')

  // Estados para modales
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [selectedSupplier, setSelectedSupplier] = useState(null)

  const fetchSuppliers = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const suppliersData = await getAllSuppliers(true) // Incluir inactivos
      setSuppliers(suppliersData)
    } catch (err) {
      console.error('Error cargando proveedores:', err)
      setError('Error al cargar los proveedores')
    } finally {
      setLoading(false)
    }
  }, [])

  // Calcular cantidad de productos por proveedor
  const calculateProductCounts = useCallback(() => {
    const counts = {}
    
    suppliers.forEach(supplier => {
      const supplierEmail = supplier.email
      let count = 0
      
      // Contar productos que tienen este proveedor en su lista
      products.forEach(product => {
        // Compatibilidad: buscar en supplierIds (nuevo) o supplierId (antiguo)
        if (product.supplierIds && Array.isArray(product.supplierIds)) {
          if (product.supplierIds.includes(supplierEmail)) {
            count++
          }
        } else if (product.supplierId === supplierEmail) {
          // Compatibilidad con datos antiguos
          count++
        }
      })
      
      counts[supplierEmail] = count
    })
    
    setSupplierProductCounts(counts)
  }, [suppliers, products])

  const applyFilters = useCallback(() => {
    let filtered = [...suppliers]

    // Filtrar por estado
    if (activeFilter === 'active') {
      filtered = filtered.filter(s => s.active !== false)
    } else if (activeFilter === 'inactive') {
      filtered = filtered.filter(s => s.active === false)
    }

    // Filtrar por búsqueda
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase()
      filtered = filtered.filter(s => {
        const name = (s.name || '').toLowerCase()
        const email = (s.email || '').toLowerCase()
        const businessName = (s.businessName || '').toLowerCase()
        const phone = (s.phone || '').toLowerCase()
        return name.includes(search) || 
               email.includes(search) || 
               businessName.includes(search) ||
               phone.includes(search)
      })
    }

    setFilteredSuppliers(filtered)
  }, [suppliers, activeFilter, searchTerm])

  // Cargar proveedores
  useEffect(() => {
    fetchSuppliers()
  }, [fetchSuppliers])

  // Calcular cantidad de productos por proveedor cuando cambian los productos o proveedores
  useEffect(() => {
    if (suppliers.length > 0 && products.length >= 0) {
      calculateProductCounts()
    }
  }, [suppliers, products, calculateProductCounts])

  // Aplicar filtros
  useEffect(() => {
    applyFilters()
  }, [applyFilters])

  const handleDelete = async () => {
    if (!selectedSupplier) return

    try {
      await deleteSupplier(selectedSupplier.email)
      setNotification({
        type: 'success',
        message: `Proveedor "${selectedSupplier.name}" desactivado correctamente`
      })
      setTimeout(() => setNotification(null), 3000)
      await fetchSuppliers()
    } catch (err) {
      console.error('Error eliminando proveedor:', err)
      setNotification({
        type: 'error',
        message: `Error al desactivar proveedor: ${err.message || 'Error desconocido'}`
      })
      setTimeout(() => setNotification(null), 5000)
    } finally {
      setShowDeleteDialog(false)
      setSelectedSupplier(null)
    }
  }

  if (loading) {
    return (
      <div className="admin-suppliers-page">
        <div className="loading-state">
          <p>Cargando proveedores...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="admin-suppliers-page">
        <div className="error-state">
          <p>{error}</p>
          <Button onClick={fetchSuppliers}>Reintentar</Button>
        </div>
      </div>
    )
  }

  return (
    <div className="admin-suppliers-page">
      <Toast
        isVisible={!!notification}
        message={notification?.message || ''}
        type={notification?.type || 'error'}
        onClose={() => setNotification(null)}
      />

      {/* Header */}
      <div className="suppliers-header">
        <div>
          <h1>Gestión de Proveedores</h1>
          <p className="suppliers-subtitle">
            Administra los proveedores y sus relaciones con productos
          </p>
        </div>
        <Link to="/admin/proveedores/nuevo">
          <Button variant="primary">
            ➕ Crear Proveedor
          </Button>
        </Link>
      </div>

      {/* Filtros */}
      <div className="suppliers-filters">
        <div className="filter-group">
          <label htmlFor="activeFilter">Estado:</label>
          <select
            id="activeFilter"
            value={activeFilter}
            onChange={(e) => setActiveFilter(e.target.value)}
            className="filter-input"
          >
            <option value="all">Todos</option>
            <option value="active">Activos</option>
            <option value="inactive">Inactivos</option>
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="searchTerm">Buscar:</label>
          <input
            type="text"
            id="searchTerm"
            placeholder="Nombre, email, razón social..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="filter-input"
          />
        </div>

        <div className="filter-group">
          <div className="filter-info">
            <span className="filter-count">
              {filteredSuppliers.length} proveedor{filteredSuppliers.length !== 1 ? 'es' : ''}
            </span>
          </div>
        </div>
      </div>

      {/* Tabla de proveedores - Desktop */}
      <div className="suppliers-table-container">
        {filteredSuppliers.length === 0 ? (
          <div className="empty-state">
            <p>
              {searchTerm || activeFilter !== 'all'
                ? 'No se encontraron proveedores con los filtros aplicados'
                : 'No hay proveedores registrados. Crea el primero para comenzar.'}
            </p>
            {!searchTerm && activeFilter === 'all' && (
              <Link to="/admin/proveedores/nuevo">
                <Button variant="primary" style={{ marginTop: '1rem' }}>
                  ➕ Crear Primer Proveedor
                </Button>
              </Link>
            )}
          </div>
        ) : (
          <table className="suppliers-table">
            <thead>
              <tr>
                <th>Proveedor</th>
                <th>Contacto</th>
                <th>Información Comercial</th>
                <th>Productos</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredSuppliers.map((supplier) => (
                <tr key={supplier.email} className={supplier.active === false ? 'inactive-row' : ''}>
                  <td>
                    <div className="supplier-name-cell">
                      <strong>{supplier.name}</strong>
                      {supplier.businessName && (
                        <small className="business-name">{supplier.businessName}</small>
                      )}
                      {supplier.taxId && (
                        <small className="tax-id">CUIT: {supplier.taxId}</small>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="contact-cell">
                      <div className="contact-item">
                        <span className="contact-label">📧</span>
                        <span>{supplier.email || '-'}</span>
                      </div>
                      <div className="contact-item">
                        <span className="contact-label">📞</span>
                        <span>{supplier.phone || '-'}</span>
                      </div>
                      {supplier.phoneAlternate && (
                        <div className="contact-item">
                          <span className="contact-label">📱</span>
                          <span>{supplier.phoneAlternate}</span>
                        </div>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="commercial-info-cell">
                      {supplier.paymentTerms && (
                        <div className="info-item">
                          <span className="info-label">Pago:</span>
                          <span>{supplier.paymentTerms}</span>
                        </div>
                      )}
                      {supplier.deliveryTime && (
                        <div className="info-item">
                          <span className="info-label">Entrega:</span>
                          <span>{supplier.deliveryTime}</span>
                        </div>
                      )}
                      {supplier.minimumOrder && (
                        <div className="info-item">
                          <span className="info-label">Mínimo:</span>
                          <span>{formatCurrency(supplier.minimumOrder)}</span>
                        </div>
                      )}
                      {supplier.rating && (
                        <div className="info-item">
                          <span className="info-label">⭐</span>
                          <span>{supplier.rating}/5</span>
                        </div>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className="product-count">
                      {supplierProductCounts[supplier.email] || 0} producto{(supplierProductCounts[supplier.email] || 0) !== 1 ? 's' : ''}
                    </span>
                  </td>
                  <td>
                    <span className={`status-badge ${supplier.active !== false ? 'active' : 'inactive'}`}>
                      {supplier.active !== false ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td>
                    <div className="supplier-actions">
                      <Link to={`/admin/proveedores/${encodeURIComponent(supplier.email)}`}>
                        <button className="action-btn" title="Ver detalle">
                          👁️
                        </button>
                      </Link>
                      <Link to={`/admin/proveedores/${encodeURIComponent(supplier.email)}/editar`}>
                        <button className="action-btn edit-btn" title="Editar">
                          ✏️
                        </button>
                      </Link>
                      {supplier.active !== false && (
                        <button
                          className="action-btn delete-btn"
                          onClick={() => {
                            setSelectedSupplier(supplier)
                            setShowDeleteDialog(true)
                          }}
                          title="Desactivar"
                        >
                          🗑️
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Cards de proveedores - Mobile */}
      <div className="suppliers-cards-container">
        {filteredSuppliers.length === 0 ? (
          <div className="empty-state">
            <p>
              {searchTerm || activeFilter !== 'all'
                ? 'No se encontraron proveedores con los filtros aplicados'
                : 'No hay proveedores registrados. Crea el primero para comenzar.'}
            </p>
            {!searchTerm && activeFilter === 'all' && (
              <Link to="/admin/proveedores/nuevo">
                <Button variant="primary" style={{ marginTop: '1rem' }}>
                  ➕ Crear Primer Proveedor
                </Button>
              </Link>
            )}
          </div>
        ) : (
          filteredSuppliers.map((supplier) => (
            <div key={supplier.email} className={`supplier-card ${supplier.active === false ? 'inactive' : ''}`}>
              <div className="supplier-card-header">
                <div>
                  <h3>{supplier.name}</h3>
                  {supplier.businessName && (
                    <p className="supplier-business-name">{supplier.businessName}</p>
                  )}
                  {supplier.taxId && (
                    <p className="supplier-tax-id">CUIT: {supplier.taxId}</p>
                  )}
                </div>
                <span className={`status-badge ${supplier.active !== false ? 'active' : 'inactive'}`}>
                  {supplier.active !== false ? 'Activo' : 'Inactivo'}
                </span>
              </div>
              <div className="supplier-card-body">
                <div className="supplier-card-section">
                  <h4 className="section-label">Contacto</h4>
                  <div className="supplier-info-row">
                    <span className="info-label">📧 Email:</span>
                    <span className="info-value">{supplier.email || '-'}</span>
                  </div>
                  <div className="supplier-info-row">
                    <span className="info-label">📞 Teléfono:</span>
                    <span className="info-value">{supplier.phone || '-'}</span>
                  </div>
                  {supplier.phoneAlternate && (
                    <div className="supplier-info-row">
                      <span className="info-label">📱 Alternativo:</span>
                      <span className="info-value">{supplier.phoneAlternate}</span>
                    </div>
                  )}
                </div>
                {(supplier.paymentTerms || supplier.deliveryTime || supplier.minimumOrder || supplier.rating) && (
                  <div className="supplier-card-section">
                    <h4 className="section-label">Información Comercial</h4>
                    {supplier.paymentTerms && (
                      <div className="supplier-info-row">
                        <span className="info-label">Pago:</span>
                        <span className="info-value">{supplier.paymentTerms}</span>
                      </div>
                    )}
                    {supplier.deliveryTime && (
                      <div className="supplier-info-row">
                        <span className="info-label">Entrega:</span>
                        <span className="info-value">{supplier.deliveryTime}</span>
                      </div>
                    )}
                    {supplier.minimumOrder && (
                      <div className="supplier-info-row">
                        <span className="info-label">Mínimo:</span>
                        <span className="info-value">{formatCurrency(supplier.minimumOrder)}</span>
                      </div>
                    )}
                    {supplier.rating && (
                      <div className="supplier-info-row">
                        <span className="info-label">⭐ Calificación:</span>
                        <span className="info-value">{supplier.rating}/5</span>
                      </div>
                    )}
                  </div>
                )}
                <div className="supplier-card-section">
                  <div className="supplier-info-row">
                    <span className="info-label">Productos:</span>
                    <span className="info-value product-count">
                      {supplierProductCounts[supplier.email] || 0} producto{(supplierProductCounts[supplier.email] || 0) !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>
              </div>
              <div className="supplier-card-actions">
                <Link to={`/admin/proveedores/${encodeURIComponent(supplier.email)}`}>
                  <Button variant="outline" size="small" style={{ width: '100%' }}>
                    👁️ Ver Detalle
                  </Button>
                </Link>
                <Link to={`/admin/proveedores/${encodeURIComponent(supplier.email)}/editar`}>
                  <Button variant="primary" size="small" style={{ width: '100%' }}>
                    ✏️ Editar
                  </Button>
                </Link>
                {supplier.active !== false && (
                  <Button
                    variant="danger"
                    size="small"
                    onClick={() => {
                      setSelectedSupplier(supplier)
                      setShowDeleteDialog(true)
                    }}
                    style={{ width: '100%' }}
                  >
                    🗑️ Desactivar
                  </Button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Dialog de confirmación de eliminación */}
      <ConfirmDialog
        isVisible={showDeleteDialog}
        title="Desactivar Proveedor"
        message={`¿Estás seguro de que deseas desactivar al proveedor "${selectedSupplier?.name}"? Esta acción no se puede deshacer, pero puedes reactivarlo editando el proveedor.`}
        confirmText="Desactivar"
        cancelText="Cancelar"
        onConfirm={handleDelete}
        onClose={() => {
          setShowDeleteDialog(false)
          setSelectedSupplier(null)
        }}
        variant="danger"
      />
    </div>
  )
}

export default AdminSuppliers

